import {
  Autocomplete,
  Chip,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

export interface BrandOption {
  /** item_id on the catalogue's brand row */
  id: string;
  /** item_name on the catalogue's brand row */
  name: string;
}

interface Props {
  options: BrandOption[];
  selectedBrandId: string;
  onSelect: (brandId: string) => void;
  /** held while a trace is loading, so the brand cannot change mid-request */
  disabled?: boolean;
}

/** How many options the list shows before the term narrows it further. */
const MAX_VISIBLE_OPTIONS = 12;

/** Brand picker for the trace view - matches on brand name or id. */
const BrandTraceSearch = ({
  options,
  selectedBrandId,
  onSelect,
  disabled,
}: Props) => {
  const selected =
    options.find((option) => option.id === selectedBrandId) ?? null;

  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Autocomplete
        data-tracker="1138"
        sx={{ width: 420 }}
        size="small"
        disabled={disabled}
        options={options}
        value={selected}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(option) => `${option.name} (${option.id})`}
        filterOptions={(all, { inputValue }) => {
          const term = inputValue.trim().toLowerCase();
          // the full brand list is long, so it is always capped
          if (!term) return all.slice(0, MAX_VISIBLE_OPTIONS);
          return all
            .filter(
              (option) =>
                option.name.toLowerCase().includes(term) ||
                option.id.startsWith(term),
            )
            .slice(0, MAX_VISIBLE_OPTIONS);
        }}
        onChange={(_, value) => onSelect(value?.id ?? "")}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ width: "100%" }}
            >
              <Typography sx={{ fontSize: "13.5px", flex: 1 }}>
                {option.name}
              </Typography>
              <Chip size="small" label={option.id} />
            </Stack>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Brand"
            placeholder="Type a brand name or id"
            color="secondary"
          />
        )}
      />
      <Chip size="small" label="Read only" />
    </Stack>
  );
};

export default BrandTraceSearch;
